import { Component, type ReactNode } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import ObservabilityPage from './pages/ObservabilityPage';
import { useAuthStore } from '@/stores/authStore';
import AuthPage from '@/pages/AuthPage';
import MainPage from '@/pages/MainPage';
import MapPage from '@/pages/MapPage';
import CameraPage from '@/pages/CameraPage';
import GoogleConnectPage from '@/pages/GoogleConnectPage';
import RegisterPage from '@/pages/RegisterPage';
import ProgramPage from '@/pages/ProgramPage';
import MissionControlPage from '@/pages/MissionControlPage';
import BootSplash from '@/components/BootSplash';

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: { componentStack?: string | null }) {
    console.error('[JARVIS] Render error:', error, info.componentStack);
  }

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) return this.props.fallback;
      return (
        <div style={{
          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
          height: '100vh', background: '#0a0a1a', color: '#e0e0e0', fontFamily: "'Science Gothic', sans-serif", padding: '2rem',
        }}>
          <h1 style={{ color: '#00d4ff', fontSize: '1.5rem', marginBottom: '1rem' }}>SYSTEM ERROR</h1>
          <p style={{ color: '#ff4444', marginBottom: '1rem' }}>{this.state.error?.message}</p>
          <button
            onClick={() => { this.setState({ hasError: false, error: null }); window.location.reload(); }}
            style={{
              padding: '0.5rem 1.5rem', background: 'rgba(0,212,255,0.1)', border: '1px solid rgba(0,212,255,0.3)',
              color: '#00d4ff', cursor: 'pointer', fontFamily: "'Science Gothic', sans-serif",
            }}
          >
            REBOOT SYSTEM
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

function ProtectedRoute({ children }: { children: ReactNode }) {
  const token = useAuthStore((state) => state.token);
  const logout = useAuthStore((state) => state.logout);
  const location = useLocation();

  // Check token validity — but DON'T logout on expiry if we have a refresh token.
  // The axios interceptor will handle refreshing automatically on the next API call.
  if (token) {
    try {
      JSON.parse(atob(token.split('.')[1]));
      // Token is decodable — let it through. If expired, the 401 interceptor
      // will refresh it on the next API call. Do NOT logout here.
    } catch {
      // Can't decode token — it's structurally invalid, not just expired
      const refreshToken = useAuthStore.getState().refreshToken;
      if (!refreshToken) {
        logout();
        return <Navigate to="/login" replace />;
      }
      // Has refresh token — let the interceptor handle it
    }
  }

  if (!token) {
    if (location.pathname !== '/login' && location.pathname !== '/') {
      localStorage.setItem('jarvis_post_login_redirect', location.pathname + location.search);
    }
    return <Navigate to="/login" replace />;
  }
  return <>{children}</>;
}

function PublicRoute({ children }: { children: ReactNode }) {
  const token = useAuthStore((state) => state.token);
  if (token) return <Navigate to="/" replace />;
  return <>{children}</>;
}

export default function App() {
  return (
    <ErrorBoundary>
      <BootSplash />
      <Routes>
        <Route
          path="/"
          element={
            <ProtectedRoute>
              <MainPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/login"
          element={
            <PublicRoute>
              <AuthPage />
            </PublicRoute>
          }
        />
        <Route
          path="/atlas"
          element={
            <ProtectedRoute>
              <MapPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/camera"
          element={
            <ProtectedRoute>
              <CameraPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/mission"
          element={
            <ProtectedRoute>
              <MissionControlPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/mission/diagnostics"
          element={
            <ProtectedRoute>
              <MissionControlPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/observability"
          element={
            <ProtectedRoute>
              <ObservabilityPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/program"
          element={
            <ProtectedRoute>
              <ProgramPage />
            </ProtectedRoute>
          }
        />
        <Route path="/ironman" element={<Navigate to="/program" replace />} />
        <Route path="/maps" element={<Navigate to="/atlas" replace />} />
        <Route path="/map" element={<Navigate to="/atlas" replace />} />
        <Route path="/connect/google" element={<GoogleConnectPage />} />
        <Route path="/register" element={<RegisterPage />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </ErrorBoundary>
  );
}

export { ErrorBoundary };
